/**
 * The building blocks of Seiri's e-mails, as MJML. One copy, used twice: the CLI composes the seeded templates from
 * these functions (`apps/cli/src/email-templates`), and the admin registers them as blocks in its GrapesJS editor -
 * so a template seeded from the repo and one assembled in the admin are made of the same parts (docs/emails.md).
 *
 * The output is restricted to what `grapesjs-mjml` can load and save back unchanged:
 * - only `mj-wrapper`, `mj-section`, `mj-column`, `mj-text`, `mj-image`, `mj-button`, `mj-spacer`, and `mj-title`,
 *   `mj-preview`, `mj-font`, `mj-style` in the head. No `mj-table`, no `mj-attributes`, no `mj-raw`;
 * - attributes written on each element, since `mj-attributes` would not survive the editor;
 * - every MJML element closed explicitly (`<mj-image …></mj-image>`). The editor parses MJML as HTML, where a custom
 *   element cannot close itself: `<mj-image />` swallowed the elements after it, `<mj-font />` the `mj-style`;
 * - a `{{#name}}…{{/name}}` section (shown only when `name` is filled, see `renderHtml`) only inside an `mj-text`,
 *   around block-level HTML, so neither the editor nor a browser's HTML parser moves its markers.
 *
 * Colours are hex literals - e-mail has no CSS custom properties - taken from the Primitives listed in
 * docs/figma.md, _Emails on page 4_.
 */

/** The CDN folder that holds `apps/cli/src/email-templates/assets/v1/`, `v2/`, … Published files never change. */
export const EMAIL_ASSETS_URL = 'https://seiri-cdn.fra1.digitaloceanspaces.com/emails'

export const emailAsset = (file: string): string => `${EMAIL_ASSETS_URL}/v1/${file}`

const COLOR = {
  canvas: '#f5f7f4', // moss/50
  card: '#ffffff', // neutral/0
  border: '#e8e8e8', // neutral/200
  accent: '#5ebcc2', // spring/500
  heading: '#2f472e', // moss/600
  body: '#505050', // neutral/900
  strong: '#2a2a2a', // neutral/950
  muted: '#707070', // neutral/800
  cta: '#15748d', // spring/700 - white on it is 5.3:1; on spring/500 it would be 2.4:1
  link: '#4f72c4', // accent/strong
  quoteRule: '#9ba19a', // moss/300
  infoBg: '#e0f2fe', // signal/info/50
  infoRule: '#49b0f0', // signal/info/300
  infoFg: '#033e75', // signal/info/700
} as const

const FONT = 'Poppins, Arial, Helvetica, sans-serif'
const FONT_URL =
  'https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,400;0,500;0,600;0,700;1,400&amp;display=swap'

/** The horizontal padding of everything inside the card; the `inset` class drops it to 24px on a phone. */
const INSET = '40px'

type Attrs = Record<string, string | undefined>
const attrs = (values: Attrs): string =>
  Object.entries(values)
    .filter((entry): entry is [string, string] => entry[1] !== undefined)
    .map(([key, value]) => ` ${key}="${value}"`)
    .join('')

/** An `mj-text` with the e-mail's body text defaults; `values` override them. */
function text(content: string, values: Attrs = {}): string {
  return `<mj-text${attrs({ 'font-family': FONT, 'font-size': '16px', 'line-height': '24px', color: COLOR.body, padding: '0', ...values })}>${content}</mj-text>`
}

/** One full-width section of the card, padded like the rest. */
function cardSection(content: string, padding = `0 ${INSET} 24px`): string {
  return `<mj-section css-class="inset" padding="${padding}"><mj-column padding="0">${content}</mj-column></mj-section>`
}

/** Wraps `html` so it renders only when the variable `when` is filled. */
const showWhen = (when: string | undefined, html: string): string => (when ? `{{#${when}}}${html}{{/${when}}}` : html)

// ---------------------------------------------------------------------------------------------------------------
// The document

/*
 * No `>` in these selectors: the admin's editor HTML-escapes `mj-style` on save, so `.inset > table` would become
 * `.inset &gt; table` and stop matching. `td[style*="40px"]` finds a section's own cell instead - the only cells whose
 * padding is the 40px inset (INSET); the cells inside a block never use it.
 */
const HEAD_STYLE = `
a { color: ${COLOR.link}; }
.quote p { margin: 0 0 8px; }
.quote p:last-child { margin-bottom: 0; }
@media only screen and (max-width: 480px) {
  .card { margin-left: 16px !important; margin-right: 16px !important; }
  .inset td[style*="40px"] { padding-left: 24px !important; padding-right: 24px !important; }
  .cta table { width: 100% !important; }
  .cta a { display: block !important; }
}`

/**
 * A whole e-mail: the canvas, the card (`header` + `body`) and the footer below it. `title` and `preview` are the
 * document title and the preheader the inbox shows after the subject; both may hold `{{variables}}`.
 */
export function emailDocument(parts: {
  title: string
  preview: string
  header: string
  body: string[]
  footer: string
}): string {
  return `<mjml lang="sk">
  <mj-head>
    <mj-title>${parts.title}</mj-title>
    <mj-preview>${parts.preview}</mj-preview>
    <mj-font name="Poppins" href="${FONT_URL}"></mj-font>
    <mj-style>${HEAD_STYLE}
    </mj-style>
  </mj-head>
  <mj-body background-color="${COLOR.canvas}" width="600px">
    <mj-section padding="0"><mj-column padding="0"><mj-spacer height="40px"></mj-spacer></mj-column></mj-section>
    <mj-wrapper css-class="card" padding="0" background-color="${COLOR.card}" border-radius="12px" border-top="4px solid ${COLOR.accent}" border-left="1px solid ${COLOR.border}" border-right="1px solid ${COLOR.border}" border-bottom="1px solid ${COLOR.border}">
      ${parts.header}
      ${parts.body.join('\n      ')}
      <mj-section padding="0"><mj-column padding="0"><mj-spacer height="16px"></mj-spacer></mj-column></mj-section>
    </mj-wrapper>
    ${parts.footer}
  </mj-body>
</mjml>
`
}

// ---------------------------------------------------------------------------------------------------------------
// Header

/** Customer-facing e-mails are headed by the premise, not by Seiri: the customer booked with the premise. */
export function headerPremise(name = '{{entityName}}'): string {
  return `<mj-section css-class="inset" padding="24px ${INSET} 20px" border-bottom="1px solid ${COLOR.border}"><mj-column padding="0">${text(name, { 'font-size': '22px', 'line-height': '28px', color: COLOR.heading })}</mj-column></mj-section>`
}

/** Account and internal e-mails carry the Seiri logo. */
export function headerSeiri(): string {
  return `<mj-section css-class="inset" padding="24px ${INSET} 20px" border-bottom="1px solid ${COLOR.border}"><mj-column padding="0"><mj-image src="${emailAsset('logo.png')}" width="70px" height="40px" align="left" alt="Seiri" padding="0"></mj-image></mj-column></mj-section>`
}

// ---------------------------------------------------------------------------------------------------------------
// Body

export type HeroTone = 'success' | 'danger' | 'info' | 'neutral'

/** What happened, at a glance: the tone's badge, a title and a lead paragraph. The first block of the body. */
export function hero(tone: HeroTone, title: string, lead?: string): string {
  return cardSection(
    `<mj-image src="${emailAsset(`badge-${tone}.png`)}" width="48px" height="48px" align="left" alt="" padding="0 0 16px"></mj-image>` +
      text(title, {
        'font-size': '24px',
        'line-height': '32px',
        color: COLOR.heading,
        padding: lead ? '0 0 8px' : '0',
      }) +
      (lead ? text(lead) : ''),
    `32px ${INSET} 24px`,
  )
}

export type DetailIcon = 'service' | 'date' | 'place' | 'person' | 'mail' | 'call'

export interface DetailRow {
  icon: DetailIcon
  label: string
  value: string
  /** Show the row only when this variable is filled - a place or an employee that was never assigned. */
  when?: string
  /** Struck through and muted: the time of a cancelled reservation. */
  struck?: boolean
}

function detailRowHtml(row: DetailRow): string {
  const valueStyle = row.struck
    ? `font-size: 16px; line-height: 24px; font-weight: 500; color: ${COLOR.muted}; text-decoration: line-through`
    : `font-size: 16px; line-height: 24px; font-weight: 500; color: ${COLOR.strong}`
  return showWhen(
    row.when,
    `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr>` +
      `<td width="32" valign="top" style="padding: 2px 0 16px 0; width: 32px"><img src="${emailAsset(`icon-${row.icon}.png`)}" width="20" height="20" alt="" style="display: block; border: 0" /></td>` +
      `<td valign="top" style="padding: 0 0 16px 0"><div style="font-size: 12px; line-height: 16px; color: ${COLOR.muted}">${row.label}</div><div style="${valueStyle}">${row.value}</div></td>` +
      `</tr></table>`,
  )
}

/** The facts of a reservation on a muted card, one icon row each. Rows can be optional (`when`). */
export function detailCard(rows: DetailRow[]): string {
  return `<mj-section css-class="inset" padding="0 ${INSET} 24px"><mj-column padding="20px 24px 4px" background-color="${COLOR.canvas}" border-radius="8px">${text(rows.map(detailRowHtml).join(''), { 'font-size': '16px' })}</mj-column></mj-section>`
}

/**
 * Text the premise wrote - a reservation's description, a cancellation reason - set apart as a quote. With `when` the
 * whole quote disappears when that variable is empty. `preLine` keeps the line breaks of plain text.
 */
export function quote(label: string, body: string, options: { when?: string; preLine?: boolean } = {}): string {
  const bodyStyle = `font-style: italic${options.preLine ? '; white-space: pre-line' : ''}`
  const html =
    `<div style="background-color: ${COLOR.canvas}; border-left: 4px solid ${COLOR.quoteRule}; border-radius: 4px; padding: 16px 20px; margin: 0 0 24px 0">` +
    `<div style="font-size: 12px; line-height: 16px; color: ${COLOR.muted}; padding-bottom: 4px">${label}</div>` +
    `<div class="quote" style="${bodyStyle}">${body}</div></div>`
  return cardSection(text(showWhen(options.when, html)), `0 ${INSET}`)
}

/** A note from Seiri itself - a link's expiry, what to do if it was not you. */
export function info(label: string, body: string): string {
  const html =
    `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color: ${COLOR.infoBg}; border-left: 4px solid ${COLOR.infoRule}; border-radius: 4px"><tr>` +
    `<td width="32" valign="top" style="padding: 16px 0 16px 20px; width: 32px"><img src="${emailAsset('icon-info.png')}" width="20" height="20" alt="" style="display: block; border: 0" /></td>` +
    `<td valign="top" style="padding: 16px 20px 16px 0"><div style="font-size: 12px; line-height: 16px; color: ${COLOR.infoFg}; padding-bottom: 4px">${label}</div>` +
    `<div style="font-size: 14px; line-height: 21px; font-weight: 500; color: ${COLOR.body}">${body}</div></td>` +
    `</tr></table>`
  return cardSection(text(html))
}

/** The one primary action. Full width on a phone. */
export function button(label: string, href: string): string {
  return cardSection(
    `<mj-button href="${href}" css-class="cta" align="left" padding="0" background-color="${COLOR.cta}" color="#ffffff" border-radius="9999px" font-family="${FONT}" font-size="14px" font-weight="700" line-height="21px" inner-padding="12px 24px">${label}</mj-button>`,
  )
}

/** How a link in the card looks, for a link built in code (E19's list of termíny) as much as for `linkLine`. */
export const EMAIL_LINK_STYLE = `color: ${COLOR.link}; text-decoration: underline`

/**
 * A secondary action as a sentence with a link: "Nemôžete prísť? Zrušiť rezerváciu". `after` continues the sentence
 * after the link, and with `when` the whole line shows only when that variable is filled - the cancel line of a
 * reservation the customer may not cancel.
 */
export function linkLine(
  lead: string,
  label: string,
  href: string,
  options: { when?: string; after?: string } = {},
): string {
  const line = `${lead} <a href="${href}" style="${EMAIL_LINK_STYLE}">${label}</a>${options.after ?? ''}`
  return cardSection(
    text(options.when ? showWhen(options.when, `<div>${line}</div>`) : line, {
      'font-size': '14px',
      'line-height': '21px',
      'font-weight': '500',
    }),
  )
}

/** Plain paragraphs in the card, for anything the other blocks do not cover. */
export function paragraph(html: string): string {
  return cardSection(text(html))
}

/** Small print in the card: a fallback link, a hint. */
export function smallPrint(html: string): string {
  return cardSection(text(html, { 'font-size': '12px', 'line-height': '16px', color: COLOR.muted }))
}

// ---------------------------------------------------------------------------------------------------------------
// Footer, below the card

const footerText = (content: string, values: Attrs = {}) =>
  text(content, { align: 'center', 'font-size': '12px', 'line-height': '16px', color: COLOR.muted, ...values })

/** Customer-facing: the premise's name and contacts, "sent through Seiri", and why the reader got it. */
export function footerCustomer(reason: string): string {
  return (
    `<mj-section css-class="inset" padding="24px ${INSET} 40px"><mj-column padding="0">` +
    footerText('{{entityName}}', {
      'font-size': '14px',
      'line-height': '21px',
      'font-weight': '500',
      color: COLOR.body,
      padding: '0 0 8px',
    }) +
    footerText('{{#entityContact}}<div style="padding-bottom: 8px">{{entityContact}}</div>{{/entityContact}}') +
    footerText(
      `Odoslané cez rezervačný systém <img src="${emailAsset('logo.png')}" width="42" height="24" alt="Seiri" style="vertical-align: middle; border: 0; margin-left: 4px" />`,
      { padding: '4px 0 12px' },
    ) +
    footerText(reason) +
    `</mj-column></mj-section>`
  )
}

/** Account and internal: the Seiri logo, what Seiri is, and why the reader got it. */
export function footerInternal(reason: string): string {
  return (
    `<mj-section css-class="inset" padding="24px ${INSET} 40px"><mj-column padding="0">` +
    `<mj-image src="${emailAsset('logo.png')}" width="56px" height="32px" align="center" alt="Seiri" padding="0 0 8px"></mj-image>` +
    footerText('Seiri · rezervačný systém pre prevádzky', { padding: '0 0 8px' }) +
    footerText(reason) +
    `</mj-column></mj-section>`
  )
}

// ---------------------------------------------------------------------------------------------------------------
// For the admin's editor

/** A block in the admin's GrapesJS editor: one of the pieces above with placeholder text to replace. */
export interface EmailEditorBlock {
  id: string
  label: string
  content: string
}

/** Every block the admin's editor offers under "Seiri", in the order a template uses them. */
export const EMAIL_EDITOR_BLOCKS: EmailEditorBlock[] = [
  { id: 'seiri-header-premise', label: 'Hlavička – prevádzka', content: headerPremise() },
  { id: 'seiri-header-seiri', label: 'Hlavička – Seiri', content: headerSeiri() },
  { id: 'seiri-hero-success', label: 'Úvod – potvrdenie', content: hero('success', 'Nadpis', 'Úvodný text.') },
  { id: 'seiri-hero-danger', label: 'Úvod – zrušenie', content: hero('danger', 'Nadpis', 'Úvodný text.') },
  { id: 'seiri-hero-info', label: 'Úvod – oznámenie', content: hero('info', 'Nadpis', 'Úvodný text.') },
  { id: 'seiri-hero-neutral', label: 'Úvod – účet', content: hero('neutral', 'Nadpis', 'Úvodný text.') },
  {
    id: 'seiri-detail-card',
    label: 'Detaily rezervácie',
    content: detailCard([
      { icon: 'service', label: 'Služba', value: 'Názov služby' },
      { icon: 'date', label: 'Termín', value: 'Dátum a čas' },
      { icon: 'place', label: 'Miesto', value: 'Miesto' },
      { icon: 'person', label: 'Zamestnanec', value: 'Meno' },
    ]),
  },
  { id: 'seiri-quote', label: 'Správa od prevádzky', content: quote('Správa od prevádzky', 'Text správy.') },
  { id: 'seiri-info', label: 'Upozornenie', content: info('Nadpis upozornenia', 'Text upozornenia.') },
  { id: 'seiri-button', label: 'Tlačidlo', content: button('Text tlačidla', '#') },
  { id: 'seiri-link-line', label: 'Odkaz vo vete', content: linkLine('Úvodná veta?', 'Text odkazu', '#') },
  { id: 'seiri-paragraph', label: 'Odstavec', content: paragraph('Text odstavca.') },
  {
    id: 'seiri-footer-customer',
    label: 'Pätička – zákazník',
    content: footerCustomer('Prečo ste dostali tento e-mail.'),
  },
  { id: 'seiri-footer-internal', label: 'Pätička – Seiri', content: footerInternal('Prečo ste dostali tento e-mail.') },
]

/**
 * What a new template starts from in the admin: the full layout around the registry's plain-text fallback, so an
 * admin-made template looks like the seeded ones without assembling it block by block.
 */
export function emailStarterDocument(plainText = ''): string {
  const escaped = plainText.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br />')
  return emailDocument({
    title: 'Seiri',
    preview: '',
    header: headerSeiri(),
    body: [paragraph(escaped || 'Text e-mailu.')],
    footer: footerInternal('Prečo ste dostali tento e-mail.'),
  })
}
